// ============================================================================
// PRINT MODULE TYPES
// ============================================================================
// The Print module (KOT printing, contract section 6): printers, Print Agents,
// pairing codes, print jobs and alerts. The module is neutral: bills,
// receipts and kitchen tickets all print through it. The API omits null
// fields, so every optional field may be missing.

export type PrintAgentStatus = 'PENDING_APPROVAL' | 'ACTIVE' | 'SUSPENDED' | 'REVOKED';
export type PrintKeyStorage = 'TPM' | 'SOFTWARE';
export type PrinterConnection = 'NETWORK' | 'USB';
export type PrinterRenderMode = 'TEXT' | 'IMAGE';
export type PrinterHealth = 'UNKNOWN' | 'ONLINE' | 'OFFLINE';
export type PrintJobStatus = 'QUEUED' | 'CLAIMED' | 'SENT' | 'PRINTED' | 'FAILED' | 'REROUTED' | 'CANCELLED';
export type PrintCopyKind = 'ORIGINAL' | 'REPRINT' | 'REROUTED';
export type PairingPurpose = 'NEW_AGENT' | 'REPLACE_AGENT';

/** Character sets an ESC/POS printer commonly supports (phase 1). */
export const PRINT_CODE_PAGES = ['PC437', 'PC850', 'PC852', 'PC858', 'WPC1252'] as const;

// ------------------------------------------------------------------ printers

export interface Printer {
  id: string;
  agentId: string;
  agentName?: string;
  agentStatus?: PrintAgentStatus;
  /** A heartbeat from the agent in the last minute. */
  agentOnline: boolean;
  printerCode: string;
  printerName: string;
  connectionType: PrinterConnection;
  host?: string;
  port: number;
  osPrinterName?: string;
  paperWidthMm: number;
  charsPerLine?: number;
  codePage: string;
  renderMode: PrinterRenderMode;
  hasCutter: boolean;
  hasBuzzer: boolean;
  supportsStatus: boolean;
  healthState: PrinterHealth;
  healthDetail?: string;
  healthAt?: string;
  /** Jobs waiting or failed and not yet handled on this printer. */
  openJobCount: number;
  isActive: boolean;
  rowVersion: number;
  createdAt?: string;
  updatedAt?: string;
}

/** An active printer for a dropdown: the department form, "Print elsewhere". */
export interface PrinterLookup {
  id: string;
  printerCode: string;
  printerName: string;
  agentName?: string;
  agentStatus?: PrintAgentStatus;
  agentOnline: boolean;
  paperWidthMm: number;
  healthState: PrinterHealth;
}

export interface CreatePrinterRequest {
  agentId: string;
  printerCode: string;
  printerName: string;
  connectionType: PrinterConnection;
  host?: string;
  port: number;
  osPrinterName?: string;
  paperWidthMm: number;
  charsPerLine?: number;
  codePage: string;
  renderMode: PrinterRenderMode;
  hasCutter: boolean;
  hasBuzzer: boolean;
  supportsStatus: boolean;
}

export interface UpdatePrinterRequest extends CreatePrinterRequest {
  isActive: boolean;
  rowVersion: number;
}

export interface PrinterQuery {
  search?: string;
  agentId?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

// -------------------------------------------------------------------- agents

export interface PrintAgent {
  id: string;
  agentName: string;
  siteName?: string;
  agentStatus: PrintAgentStatus;
  keyStorage: PrintKeyStorage;
  machineName?: string;
  osDescription?: string;
  agentVersion?: string;
  registeredAt?: string;
  approvedAt?: string;
  revokedAt?: string;
  statusReason?: string;
  replacedByAgentId?: string;
  replacedByAgentName?: string;
  lastSeenAt?: string;
  lastSeenIp?: string;
  online: boolean;
  printerCount: number;
  offlinePrinterCount: number;
  rowVersion: number;
  createdAt?: string;
}

export interface PrintAgentQuery {
  search?: string;
  status?: PrintAgentStatus;
  page?: number;
  pageSize?: number;
}

export interface UpdatePrintAgentRequest {
  agentName: string;
  siteName?: string;
  rowVersion: number;
}

export interface CreatePairingCodeRequest {
  agentName: string;
  siteName?: string;
  /** Pair a machine that takes over this agent's printers (new PC, reinstall). */
  replaceAgentId?: string;
  requireApproval: boolean;
}

/** A new pairing code. The code is shown once, here, and never stored. */
export interface PairingCodeCreated {
  id: string;
  code: string;
  expiresAt: string;
  agentName: string;
  purpose: PairingPurpose;
}

export interface PairingCode {
  id: string;
  codeHint: string;
  purpose: PairingPurpose;
  agentName: string;
  siteName?: string;
  replaceAgentId?: string;
  replaceAgentName?: string;
  requireApproval: boolean;
  expiresAt: string;
  createdByName?: string;
  createdAt?: string;
}

// ---------------------------------------------------------------------- jobs

export interface PrintJobEvent {
  id: string;
  eventType: string;
  agentId?: string;
  agentName?: string;
  printerId?: string;
  printerName?: string;
  attempt?: number;
  errorCode?: string;
  detail?: string;
  occurredAt: string;
  byName?: string;
}

/** A row of the Print Queue. */
export interface PrintJob {
  id: string;
  jobKey: string;
  documentType: 'KOT' | 'BILL' | 'RECEIPT' | 'TEST';
  sourceModule: string;
  sourceId?: string;
  /** The KOT number for a kitchen ticket. */
  sourceReference?: string;
  /** The department for a kitchen ticket. */
  sourceLabel?: string;
  scopeId?: string;
  printerId: string;
  printerName?: string;
  /** Set on a rerouted job: the printer it was meant for. */
  originalPrinterId?: string;
  originalPrinterName?: string;
  backupPrinterId?: string;
  agentName?: string;
  agentOnline: boolean;
  copyKind: PrintCopyKind;
  copyNumber: number;
  copyCount: number;
  reprintNumber?: number;
  possibleDuplicate: boolean;
  jobStatus: PrintJobStatus;
  attempts: number;
  failedAttempts: number;
  nextAttemptAt?: string;
  sentAt?: string;
  printedAt?: string;
  failedAt?: string;
  lastErrorCode?: string;
  lastError?: string;
  reroutedToJobId?: string;
  reroutedFromJobId?: string;
  cancelledAt?: string;
  cancelReason?: string;
  requestReason?: string;
  requestedByLabel?: string;
  resolvedAt?: string;
  resolvedByName?: string;
  resolutionNote?: string;
  createdAt: string;
  rowVersion: number;
  events?: PrintJobEvent[];
}

/** OPEN = everything not yet handled (queued, printing, failed and rerouted unresolved). */
export type PrintJobStatusFilter = 'OPEN' | 'ALL' | PrintJobStatus;

export interface PrintJobQuery {
  status?: PrintJobStatusFilter;
  printerId?: string;
  scopeId?: string;
  /** ISO 8601 with offset. */
  from?: string;
  to?: string;
  /** Matches the KOT number, the department and the printer name. */
  search?: string;
  page?: number;
  pageSize?: number;
}

// -------------------------------------------------------------------- alerts

export interface PrintAgentAlert {
  agentId: string;
  agentName: string;
  siteName?: string;
  agentStatus: PrintAgentStatus;
  lastSeenAt?: string;
  printerCount: number;
  waitingJobCount: number;
}

/** What the staff screens poll to show print trouble. */
export interface PrintAlerts {
  failedJobs: PrintJob[];
  offlineAgents: PrintAgentAlert[];
  queuedCount: number;
}

// ---------------------------------------------------- DineOps side of printing

/**
 * A kitchen ticket's printing at a glance. AGENT: the Print Agent prints it and
 * status says how far it got. BROWSER: no usable printer on the department, so
 * it shows on the kitchen screen and prints from there (D-P10).
 */
export interface DineKotPrintSummary {
  mode: 'AGENT' | 'BROWSER';
  /** Worst live job state: FAILED, QUEUED, PRINTING, SENT, PRINTED; CANCELLED when all withdrawn; absent in BROWSER mode. */
  status?: 'FAILED' | 'QUEUED' | 'PRINTING' | 'SENT' | 'PRINTED' | 'CANCELLED';
  printerName?: string;
  error?: string;
  /** True when the ticket went to the backup printer. */
  rerouted: boolean;
  jobCount: number;
  reprintCount: number;
}

/** A job as DineOps sees it beside the ticket. */
export interface PrintJobBrief {
  id: string;
  sourceId?: string;
  printerId: string;
  printerName?: string;
  jobStatus: PrintJobStatus;
  copyKind: PrintCopyKind;
  copyNumber: number;
  reprintNumber?: number;
  possibleDuplicate: boolean;
  lastError?: string;
  reroutedToJobId?: string;
  sentAt?: string;
  createdAt?: string;
  resolved: boolean;
}

/** GET /dine/kots/{id}/print-status, and the result of a reprint. */
export interface DineKotPrintStatus {
  kotId: string;
  kotNumber: string;
  summary: DineKotPrintSummary;
  jobs: PrintJobBrief[];
}

/** One order channel's printer for a department (DO-307). */
export interface StationChannelPrinter {
  stationId?: string;
  orderChannel: 'DINE_IN' | 'ROOM_SERVICE' | 'TAKEAWAY';
  printerId: string;
  printerName?: string;
}
